/** Enquete do dia — cliente. Online, como o ponto de encontro: votar exige rede. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { LugarDaAtividade } from '../roteiro/LocalField';

export interface OpcaoDaEnquete {
  id: string;
  text: string;
  placeId: string | null;
  placeAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  votes: number;
  voters: { memberId: string; name: string }[];
}

export interface Enquete {
  id: string;
  dayId: string;
  dayDate: string;
  question: string;
  createdBy: { memberId: string; name: string };
  closesAt: string;
  closedAt: string | null;
  isOpen: boolean;
  options: OpcaoDaEnquete[];
  totalVotes: number;
  myOptionId: string | null;
  winnerOptionId: string | null;
  activityId: string | null;
  canClose: boolean;
}

export interface NovaOpcao {
  id: string;
  text: string;
  sortOrder: number;
  lugar: LugarDaAtividade | null;
}

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init, headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

export const enqueteApi = {
  listar: (tripId: string, dayId?: string) => pedir<{ polls: Enquete[] }>(`/api/v1/trips/${tripId}/polls${dayId ? `?dayId=${encodeURIComponent(dayId)}` : ''}`),
  criar: (tripId: string, input: { id: string; dayId: string; question: string; closesAt: string; options: NovaOpcao[] }) =>
    pedir<{ poll: Enquete }>(`/api/v1/trips/${tripId}/polls`, {
      method: 'POST',
      body: JSON.stringify({
        id: input.id,
        dayId: input.dayId,
        question: input.question,
        closesAt: input.closesAt,
        options: input.options.map((o) => ({ id: o.id, text: o.text, sortOrder: o.sortOrder, ...(o.lugar ?? {}) })),
      }),
    }),
  votar: (tripId: string, pollId: string, optionId: string) => pedir<{ poll: Enquete }>(`/api/v1/trips/${tripId}/polls/${pollId}/vote`, { method: 'PUT', body: JSON.stringify({ optionId }) }),
  encerrar: (tripId: string, pollId: string) => pedir<{ poll: Enquete }>(`/api/v1/trips/${tripId}/polls/${pollId}/close`, { method: 'POST' }),
  virarAtividade: (tripId: string, pollId: string, input: { id: string; startTime?: string; idempotencyKey: string }) =>
    pedir<{ activityId: string; poll: Enquete }>(`/api/v1/trips/${tripId}/polls/${pollId}/to-activity`, {
      method: 'POST',
      headers: { 'idempotency-key': input.idempotencyKey },
      body: JSON.stringify({ id: input.id, ...(input.startTime ? { startTime: input.startTime } : {}) }),
    }),
};

/** As enquetes (da viagem, ou só de um dia) — relê a cada 20 s enquanto a tela está aberta. */
export function useEnquetes(tripId: string, dayId?: string | null, ligado = true) {
  const ativo = ligado && (dayId === undefined || !!dayId);
  return useQuery({
    queryKey: ['enquetes', tripId, dayId ?? 'todas'],
    queryFn: () => enqueteApi.listar(tripId, dayId ?? undefined),
    enabled: ativo,
    refetchInterval: ativo ? 20_000 : false,
    staleTime: 10_000,
  });
}

export function useInvalidarEnquetes(tripId: string) {
  const cliente = useQueryClient();
  useEffect(() => {
    const ao = () => void cliente.invalidateQueries({ queryKey: ['enquetes', tripId] });
    window.addEventListener('nv:enquete-mudou', ao);
    return () => window.removeEventListener('nv:enquete-mudou', ao);
  }, [cliente, tripId]);
  return () => window.dispatchEvent(new Event('nv:enquete-mudou'));
}
